import { type Dispatch, type SetStateAction } from 'react';
import { extendTailwindMerge, validators } from 'tailwind-merge';
import { type ClassValue, clsx } from 'clsx';
import { ulid } from 'ulid';
import { type PaginationType } from '@/types/pagination';
import { DAMNodeType, NodeType } from '@/types/tree';
import { FileType } from '@/types/fileType';

const twMerge = extendTailwindMerge<'icon-size'>({
  extend: {
    classGroups: {
      'icon-size': [{ icon: [validators.isAny] }],
    },
  },
});

export function cn(...inputs: Array<ClassValue>) {
  return twMerge(clsx(inputs));
}

export function capitalizeFileExtension(fileName: string): string {
  // Extract the extension
  const lastDotIndex = fileName.lastIndexOf('.');

  // If there's no extension, return the original file name
  if (lastDotIndex === -1) {
    return fileName;
  }

  // Get the file name and extension
  const namePart = fileName.substring(0, lastDotIndex);
  const extension = fileName.substring(lastDotIndex + 1);

  // Capitalize the extension and rebuild the file name
  const capitalizedExtension = extension.toUpperCase();
  return `${namePart}.${capitalizedExtension}`;
}

/** A CSV metadata row is keyed by the original filename, extension included; `name` alone may
 * already be extensionless. */
export function getUploadCsvKey(entity: { name: string; originalName?: string | null }): string {
  return capitalizeFileExtension(entity.originalName ?? entity.name);
}

export const formatCount = (count: number, decimals = 2) => {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(decimals)}M`;
  }

  if (count >= 10_000) {
    return `${(count / 1_000).toFixed(decimals)}K`;
  }

  return `${count}`;
};

export const transformPath = (path: string | Array<string>) => {
  const parts = Array.isArray(path)
    ? path.filter((part) => part !== '')
    : path
        .split('/')
        .map((part) => part.trim())
        .filter(Boolean);
  const breadcrumbs = parts.slice(0, -1).map((name) => ({
    id: ulid(),
    name,
  }));

  const lastItem = parts.length > 0 ? { id: ulid(), name: parts[parts.length - 1] } : undefined;

  return { breadcrumbs, lastItem };
};

/**
 * Whether an asset can still be selected.
 *
 * `departed` and `deleted` are set by a live broadcast for a row that is still on screen:
 * the row holds its place until the viewer refreshes, but the asset has left the list or
 * stopped existing. `pointer-events-none` only stops the mouse - select-all and keyboard
 * ranges work on the data and would sweep these up, then act on them and fail. Every
 * selection path has to agree, so they all ask this.
 */
export const isSelectableAsset = (asset: { departed?: string; deleted?: boolean }) => !asset.departed && !asset.deleted;

export const isAsset = (type?: string | DAMNodeType | NodeType | FileType | null) => {
  return type
    ? type === DAMNodeType.Asset ||
        type === NodeType.Assets ||
        type === FileType.Image ||
        type === FileType.Video ||
        type === FileType.Document
    : true;
};

export const getCorrectPageNumber = (pagination: PaginationType, setPageReady: Dispatch<SetStateAction<boolean>>) => {
  let pageNumber: number;
  // The page number is too high and can't show the available assets.
  if (
    pagination.offset > pagination.count ||
    (pagination.offset === pagination.count && pagination.offset !== 0 && pagination.count !== 0)
  ) {
    pageNumber = Math.max(0, Math.ceil(pagination.count / pagination.limit) - 1);
  } else {
    // The page number is correct based on the available assets.
    pageNumber = Math.floor(pagination.offset / pagination.limit);
    setPageReady(true);
  }
  return pageNumber;
};

export const sanitizeRedirectUrl = (url?: string): string => {
  if (!url) {
    return '/';
  }

  return url.startsWith('/') && !url.startsWith('//') ? url : '/';
};
